import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SpinningIco } from "@/components/brand/SpinningIco";
import { Wordmark } from "@/components/brand/Wordmark";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

/**
 * /login - magic-link sign-in for admins and venue partners.
 *
 * Players sign up in the mobile app, not here. If someone without an
 * invite tries to log in with an email we don't have a profile for,
 * they'll land on /dashboard and see a "you need an invite" screen
 * rather than being rejected at sign-in - we don't leak which emails
 * are in the system.
 */
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";

  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Already signed in? Skip the form and go straight to the dashboard.
  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("That doesn't look like a valid email address.");
      return;
    }

    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    setSubmitting(false);

    if (authError) {
      setError(authError.message);
      return;
    }
    setSent(true);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
      {/* Volt glow backdrop */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[640px] opacity-30 blur-3xl"
        style={{
          backgroundImage:
            "radial-gradient(closest-side at 50% 0%, hsl(var(--volt)), transparent 70%)",
        }}
      />

      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="mb-10 flex flex-col items-center gap-3">
          <SpinningIco size={120} />
          <Wordmark size="text-3xl" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sm:p-8">
          {welcome && !sent && (
            <div className="mb-5 rounded-lg border border-volt/40 bg-volt-subtle p-3 text-xs text-foreground">
              <p className="font-semibold">Welcome to Whistll.</p>
              <p className="mt-1 text-muted-foreground">
                You've been invited. Enter your email to receive a sign-in
                link - use the same email your venue application was sent
                from.
              </p>
            </div>
          )}

          {!sent ? (
            <>
              <div className="mb-5">
                <h1 className="text-2xl font-semibold tracking-tight">
                  Sign in
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  For venue partners. Players sign up in the app.
                </p>
              </div>

              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium">Work email</span>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@yourvenue.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </label>

                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button type="submit" size="lg" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" aria-hidden />
                      Send me a sign-in link
                    </>
                  )}
                </Button>
              </form>

              <p className="mt-5 text-center text-xs text-muted-foreground">
                No passwords. We'll email you a one-tap sign-in link that
                works for 60 minutes.
              </p>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-volt text-primary-foreground">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Check your email</h2>
                <p className="text-sm text-muted-foreground">
                  We've sent a sign-in link to{" "}
                  <span className="font-semibold text-foreground">
                    {email}
                  </span>
                  . Tap it to continue.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Wrong email? Try again
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Running a venue and haven't applied yet?{" "}
          <a href="/venues" className="underline underline-offset-2 hover:text-foreground">
            Apply here
          </a>
          .
        </p>
      </div>
    </div>
  );
}
